import type {
  E_LoyaltyCardStatus,
  E_LoyaltyStatus,
} from '@enable-tech/shared-types/modules/loyaltyProgram/common/backend/enums';
import type { I_Coupon } from '@fe/types/modules/loyaltyProgram/coupon/backend/responses/schemas';
import type { SetStateAction } from 'preact/compat';
import type { Dispatch } from 'preact/hooks';

import type { E_PreferredLanguage } from './Shopify';

export interface I_CustomerLoyaltyContext {
  customerLoyalty: I_CustomerLoyaltyResponse | null;
  setCustomerLoyalty: (data: I_CustomerLoyaltyResponse | null) => void;
  pointsToRedeem: number;
  setPointsToRedeem: Dispatch<SetStateAction<number>>;
}

export interface I_CustomerLoyaltyResponse {
  basicInfo: I_EnableBasicInfo;
  programs: I_EnableLoyaltyInfo;
}

export enum E_CustomerLoyaltyError {
  NOT_FOUND = 'NOT_FOUND',
  UNKNOWN = 'UNKNOWN',
}

export interface I_EnableBasicInfo {
  enableId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  orderRate: number;
  amountSpent?: number;
  pointsRate?: number;
  loyaltyCardStatus: E_LoyaltyCardStatus;
  loyaltyStatus: E_LoyaltyStatus;
  currency: string;
  language: E_PreferredLanguage;
  passLink?: string;
}

export type T_CouponsInfo = I_EnableLoyaltyPointsInfo & { availableCoupons: I_Coupon[] };

export interface I_EnableLoyaltyInfo {
  loyaltyTier: I_EnableLoyaltyTierInfo;
  flexiblePoints: I_EnableLoyaltyPointsInfo;
  coupons: T_CouponsInfo;
  pointsRate: number;
  amountSpent: number;
  orderRate: number;
  tierLevelingUpMethod?: E_TierLevellingUpMethod;
}

export interface I_EnableLoyaltyTierInfo {
  currentTier: I_LoyaltyTier | null;
  nextTier: I_LoyaltyTier | null;
  remainingRequirementsCurrentTier?: string;
  remainingRequirementsUpperTier?: string;
  tierLevelingUpMethod: E_TierLevellingUpMethod;
  redemptions: I_EnableRedemption[];
  benefitsDescription: string;
  pointsBalance: number;
}

export interface I_EnableLoyaltyPointsInfo {
  availablePoints: number;
  pointsBalanceTitleEn?: string;
  pointsBalanceTitleAr?: string;
  exchangeRate: number;
  redemptions: I_EnableRedemption[];
  currency: string;
}

export interface I_EnableRedemption {
  title: string;
  redemptionId: string;
  type: E_EnableRedemptionType;
  value: number;
  appliedOn: E_EnableRedemptionAppliedOn;
  discountCode: string;
  microsDiscountId?: number;
  ordablePromotionId?: number;
  remainingUses: number;
  maxUses: number;
  isAutomatic: boolean;
  minimumAmountToRedeem?: number;
  menuItem?: I_EnableMenuItem;
  redemptionSource: E_EnableRedemptionSource;
  usageType: E_MaximumUsageType;
  // Set when the code only discounts products in one Shopify collection.
  shopifyCollectionId?: string | null;
}

export interface I_EnableMenuItem {
  integrationInfo?: {
    shopify?: {
      productId: number;
      variantId: number;
    };
  };
}

export enum E_EnableRedemptionType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
}

export enum E_EnableRedemptionAppliedOn {
  CART = 'cart',
  PRODUCT = 'product',
  DELIVERY = 'delivery',
}

export enum E_EnableRedemptionSource {
  TIER_PROGRAM = 'TierProgram',
  COUPON_PROGRAM = 'CouponProgram',
  PUNCH_CARD_PROGRAM = 'PunchCardProgram',
}

export enum E_TierLevellingUpMethod {
  ORDER_RATE = 'order rate',
  AMOUNT_SPENT = 'amount spent',
  BOTH = 'order rate and amount spent',
  POINTS_RATE = 'points rate',
}

export interface I_LoyaltyTier {
  nameEn: string;
  nameAr: string;
  id: string;
  enableTierId: string;
  orderRateThreshold?: number;
  amountSpentThreshold?: number;
  pointsRateThreshold?: number | null; // required if company.loyaltyProgramConfig.tierLevellingUpMethod is "points rate"
}

export type T_LoyaltyTier = I_CustomerLoyaltyResponse['programs']['loyaltyTier'];

export type T_CustomerLoyaltyBasicInfo = I_CustomerLoyaltyResponse['basicInfo'];

export interface I_LoyaltyTierAccordionProps {
  loyaltyTier: T_LoyaltyTier;
  basicInfo: T_CustomerLoyaltyBasicInfo;
}

export type T_CustomerLoyaltyResponsePrograms = I_CustomerLoyaltyResponse['programs'] | null;

export interface I_ErrorResponse {
  error: string;
}

export enum E_ProgressTypeVariable {
  AMOUNT_SPENT_PERCENTAGE = 'amountSpentPercentage',
  ORDER_RATE_PERCENTAGE = 'orderRatePercentage',
  POINTS_RATE_PERCENTAGE = 'pointsRatePercentage',
}

export type T_ProgressType = {
  percentage: number;
  variable: E_ProgressTypeVariable;
};

export enum E_MaximumUsageType {
  UNLIMITED = 'unlimited',
  LIMITED = 'limited',
}

export type T_RedemptionsProps = {
  redemptions?: I_EnableRedemption[];
  showRemainingUsages?: boolean;
};

export interface I_LoyaltyCardProps {
  loading: boolean;
}
